interface DayHours {
  firstHalf: { start: string; end: string };
  secondHalf: { start: string; end: string };
  isClosed: boolean;
}

interface WorkingHours {
  monday: DayHours;
  tuesday: DayHours;
  wednesday: DayHours;
  thursday: DayHours;
  friday: DayHours;
  saturday: DayHours;
  sunday: DayHours;
}

export interface MealDaySchedule {
  day: string;
  startsAt: string;
  endsAt: string;
}

export interface ScheduleValidationResult {
  isValid: boolean;
  message?: string;
}

/**
- Helper to get date/time components strictly in EST (America/New_York) without local timezone skew
 */
const getESTDateTime = (dateStr?: string, timeStr?: string) => {
  let year: number, month: number, day: number, hour: number, minute: number;

  if (dateStr && timeStr) {
    const [y, m, d] = dateStr.split('-').map(val => parseInt(val, 10));
    const [h, min] = timeStr.split(':').map(val => parseInt(val, 10));
    
    year = y;
    month = m - 1; // JS months are 0-indexed
    day = d;
    hour = isNaN(h) ? 0 : h;
    minute = isNaN(min) ? 0 : min;

    const targetString = `${dateStr}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00-04:00`;
    const dateObj = new Date(targetString);

    return {
      estDateObject: dateObj,
      weekday: dateObj.toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'long' }).toLowerCase(),
      timeString: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
    };
  } else {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      weekday: 'long',
      hour12: false,
    };

    const formatter = new Intl.DateTimeFormat('en-US', options);
    const parts = formatter.formatToParts(now);
    const partMap: Record<string, string> = {};
    parts.forEach(p => { partMap[p.type] = p.value; });

    year = parseInt(partMap['year'], 10);
    month = parseInt(partMap['month'], 10) - 1;
    day = parseInt(partMap['day'], 10);
    hour = parseInt(partMap['hour'], 10);
    if (hour === 24) hour = 0;
    minute = parseInt(partMap['minute'], 10);

    const estDateObject = new Date(`${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00-04:00`);

    return {
      estDateObject,
      weekday: (partMap['weekday'] || '').toLowerCase(),
      timeString: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
    };
  }
};

/**
- Validates scheduled pickup date/time against restaurant working hours in EST
 */
export const validateScheduleTime = (
  date: string,
  time: string,
  workingHours: WorkingHours
): ScheduleValidationResult => {
  const selectedEST = getESTDateTime(date, time);
  const currentEST = getESTDateTime();
  
  if (selectedEST.estDateObject <= currentEST.estDateObject) {
    return {
      isValid: false,
      message: 'Pickup time must be in the future'
    };
  }
  
  const dayName = selectedEST.weekday as keyof WorkingHours;
  const dayHours = workingHours[dayName];
  
  if (!dayHours) {
    return { isValid: false, message: 'Invalid date selected' };
  }
  
  if (dayHours.isClosed) {
    const dayCapitalized = dayName.charAt(0).toUpperCase() + dayName.slice(1);
    return { isValid: false, message: `Restaurant is closed on ${dayCapitalized}s` };
  }
  
  const { firstHalf, secondHalf } = dayHours;
  const targetTime = selectedEST.timeString;

  const isInFirstHalf = firstHalf.start && firstHalf.end &&
    targetTime >= firstHalf.start && targetTime <= firstHalf.end;
    
  const isInSecondHalf = secondHalf.start && secondHalf.end &&
    targetTime >= secondHalf.start && targetTime <= secondHalf.end;

  if (!isInFirstHalf && !isInSecondHalf) {
    const hours = formatWorkingHours(dayHours);
    return { isValid: false, message: `Restaurant is open ${hours}` };
  }
  
  return { isValid: true };
};

const formatWorkingHours = (dayHours: DayHours): string => {
  const parts: string[] = [];
  if (dayHours.firstHalf.start && dayHours.firstHalf.end) {
    parts.push(`${formatTime(dayHours.firstHalf.start)} to ${formatTime(dayHours.firstHalf.end)}`);
  }
  if (dayHours.secondHalf.start && dayHours.secondHalf.end) {
    parts.push(`${formatTime(dayHours.secondHalf.start)} to ${formatTime(dayHours.secondHalf.end)}`);
  }
  return parts.join(' and ');
};

const formatTime = (time: string): string => {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

/**
- Checks cart items against scheduled date and time strictly evaluated in EST.
 */
export const checkCartItemsForSchedule = (cartItems: any[], scheduledDate: string, scheduledTime: string) => {
  const selectedEST = getESTDateTime(scheduledDate, scheduledTime);
  const dayName = selectedEST.weekday;
  
  const invalidItems: any[] = [];
  const validItems: any[] = [];

  cartItems.forEach((cartItem) => {
    const item = cartItem.item || cartItem;
    const mealTimes = item.mealTimes || item.meal_times || cartItem.mealTimes || [];
    
    if (!mealTimes || mealTimes.length === 0 || item.MealName === 'All Day') {
      validItems.push(cartItem);
      return;
    }

    let isAvailableInSchedule = false;

    mealTimes.forEach((mt: any) => {
      const type = (mt.mealType || mt.type || "").toUpperCase();
      const startsAt = mt.startsAt;
      const endsAt = mt.endsAt;
      const days = mt.days;

      const isLimitedTime = 
        type === 'LIMITED_TIME' || 
        (startsAt && (startsAt.includes('T') || startsAt.includes('-'))) || 
        (endsAt && (endsAt.includes('T') || endsAt.includes('-')));

      if (isLimitedTime && startsAt && endsAt) {
        const startDate = new Date(startsAt);
        const endDate = new Date(endsAt);
        if (selectedEST.estDateObject >= startDate && selectedEST.estDateObject <= endDate) {
          isAvailableInSchedule = true;
        }
      } else if (type === 'DAYS' && Array.isArray(days) && days.length > 0) {
        if (typeof days[0] === 'object' && days[0] !== null && 'day' in days[0]) {
          const daySched = (days as MealDaySchedule[]).find(
            (d) => d.day.toLowerCase() === dayName
          );

          if (daySched && daySched.startsAt && daySched.endsAt) {
            if (scheduledTime >= daySched.startsAt && scheduledTime <= daySched.endsAt) {
              isAvailableInSchedule = true;
            }
          }
        } else {
          const matchesDay = (days as string[]).map((d: string) => d.toLowerCase()).includes(dayName);
          const isWithinTime = startsAt && endsAt && scheduledTime >= startsAt && scheduledTime <= endsAt;
          if (matchesDay && isWithinTime) {
            isAvailableInSchedule = true;
          }
        }
      } else {
        if (startsAt && endsAt) {
          if (scheduledTime >= startsAt && scheduledTime <= endsAt) {
            isAvailableInSchedule = true;
          }
        } else {
          isAvailableInSchedule = true;
        }
      }
    });

    if (isAvailableInSchedule) {
      validItems.push(cartItem);
    } else {
      invalidItems.push(cartItem);
    }
  });

  return { validItems, invalidItems };
};